/**
 * Material Planner — 只读 TodayDecision，映射为具体素材参数
 *
 * 禁止：if(day==5)、if(platform...)、if(taskType...)
 * 只允许：把 decision 里的 intent 映射到 scene/subtype 字典
 */
import type { PhotoIntent } from './types';
import type { ExecutionJob, MaterialPlan, MaterialPlanItem, TodayDecision } from './types';

/** photoIntent → 场景参数（纯字典映射，非业务判断） */
const PHOTO_INTENT_MAP: Record<
  PhotoIntent,
  { scene: string; photoSubtype: string; usage: string; label: string }
> = {
  selfie: { scene: 'friend_candid', photoSubtype: 'selfie', usage: 'moments', label: '手机自拍' },
  friend_candid: {
    scene: 'friend_candid',
    photoSubtype: 'friend_angle',
    usage: 'moments',
    label: '朋友路拍',
  },
  life: { scene: 'daily_life', photoSubtype: 'life', usage: 'moments', label: '日常生活' },
  commercial_pose: {
    scene: 'work',
    photoSubtype: 'work_daily',
    usage: 'douyin',
    label: '工作日常',
  },
};

const SHORT_VIDEO_MAP = {
  scene: 'friend_candid_video',
  motion: 'walk',
  usage: 'dm_icebreaker',
  label: '朋友路拍无声影片',
};

const TALKING_VIDEO_DEFAULT = {
  usage: 'douyin',
  label: '角色口播影片',
};

function planPhoto(job: ExecutionJob, decision: TodayDecision): MaterialPlanItem {
  const intent = decision.materials.photo.photoIntent || 'friend_candid';
  const mapped = PHOTO_INTENT_MAP[intent];
  return {
    jobType: 'photo',
    order: job.order,
    scene: mapped.scene,
    photoSubtype: mapped.photoSubtype,
    count: decision.materials.photo.count,
    usage: mapped.usage,
    reason: job.reason,
  };
}

function planShortVideo(job: ExecutionJob): MaterialPlanItem {
  return {
    jobType: 'short_video',
    order: job.order,
    scene: SHORT_VIDEO_MAP.scene,
    motion: SHORT_VIDEO_MAP.motion,
    count: 1,
    usage: SHORT_VIDEO_MAP.usage,
    reason: job.reason,
  };
}

function planVoice(job: ExecutionJob): MaterialPlanItem {
  return {
    jobType: 'voice',
    order: job.order,
    scene: 'voice',
    count: 1,
    usage: 'dm_icebreaker',
    reason: job.reason,
  };
}

function planTalkingVideo(job: ExecutionJob): MaterialPlanItem {
  return {
    jobType: 'talking_video',
    order: job.order,
    scene: 'talking',
    count: 1,
    usage: TALKING_VIDEO_DEFAULT.usage,
    reason: job.reason,
  };
}

const JOB_PLANNERS: Record<
  ExecutionJob['type'],
  (job: ExecutionJob, decision: TodayDecision) => MaterialPlanItem
> = {
  photo: planPhoto,
  short_video: (job) => planShortVideo(job),
  voice: (job) => planVoice(job),
  talking_video: (job) => planTalkingVideo(job),
};

/** 从 TodayDecision + ExecutionPlan 生成素材计划（无额外业务 if） */
export function buildMaterialPlan(decision: TodayDecision, queue: ExecutionJob[]): MaterialPlan {
  const items = queue.map((job) => JOB_PLANNERS[job.type](job, decision));

  if (decision.text.dm_opener.enabled && decision.text.dm_opener.count > 0) {
    items.push({
      jobType: 'voice',
      order: items.length + 1,
      scene: 'text_dm_opener',
      textType: 'dm_opener',
      count: decision.text.dm_opener.count,
      usage: 'active_outreach',
      reason: decision.text.dm_opener.reason,
    });
  }

  if (decision.text.moments.enabled && decision.text.moments.count > 0) {
    items.push({
      jobType: 'voice',
      order: items.length + 1,
      scene: 'text_moments',
      textType: 'moments',
      count: decision.text.moments.count,
      usage: 'moments',
      reason: decision.text.moments.reason,
    });
  }

  if (decision.text.lead_hook.enabled && decision.text.lead_hook.count > 0) {
    items.push({
      jobType: 'voice',
      order: items.length + 1,
      scene: 'lead_hook',
      count: decision.text.lead_hook.count,
      usage: 'lead_magnet',
      reason: decision.text.lead_hook.reason,
    });
  }

  return {
    characterId: decision.characterId,
    decisionDate: decision.decisionDate,
    items,
  };
}

/** 将 MaterialPlan 映射为 daily_material_packs.materials 展示结构 */
export function materialPlanToPackItems(plan: MaterialPlan) {
  return plan.items.map((item, i) => ({
    material_request_id: `req_${String(i + 1).padStart(3, '0')}`,
    material_type: item.jobType,
    scene: item.scene,
    usage_scene: item.usage,
    business_purpose: item.reason,
    status: 'pending',
    count: item.count,
    photo_subtype: item.photoSubtype,
    motion: item.motion,
    text_type: item.textType,
  }));
}

/** 勾选/扣点/进度用的稳定键（不新增业务判断） */
export function materialItemKey(item: MaterialPlanItem): string {
  if (item.textType) return `text:${item.textType}`;
  if (item.scene === 'lead_hook') return 'lead_hook';
  return `job:${item.order}:${item.jobType}`;
}

export function materialItemLabel(item: MaterialPlanItem): string {
  if (item.textType === 'dm_opener') return `私信破冰话术 × ${item.count || 1}`;
  if (item.textType === 'moments') return `朋友圈文案 × ${item.count || 1}`;
  if (item.scene === 'lead_hook') return '引流钩子';
  if (item.jobType === 'photo') return `角色生活照 × ${item.count || 1}`;
  if (item.jobType === 'short_video') return '无声生活影片';
  if (item.jobType === 'talking_video') return '角色口播影片';
  if (item.jobType === 'voice') return '角色配音';
  return item.scene;
}

/** 扣点动作 + 预估耗时（给确认层展示，不改变生成逻辑） */
export function materialItemBilling(item: MaterialPlanItem): {
  action: string;
  units: number;
  eta: string;
} {
  const count = Math.max(1, item.count || 1);
  if (item.textType) {
    return { action: 'gen_material_text', units: count, eta: '约 1 分钟' };
  }
  if (item.scene === 'lead_hook') {
    return { action: 'gen_lead_hook', units: 1, eta: '约 1 分钟' };
  }
  if (item.jobType === 'photo') {
    return { action: 'gen_material_photo', units: count, eta: `约 ${Math.max(1, count) * 25} 秒` };
  }
  if (item.jobType === 'short_video') {
    return { action: 'gen_material_silent', units: 1, eta: '约 2-5 分钟' };
  }
  if (item.jobType === 'talking_video' || item.jobType === 'voice') {
    return { action: 'gen_material_talking', units: 1, eta: '约 1-3 分钟' };
  }
  return { action: 'gen_daily_pack', units: 1, eta: '约数分钟' };
}
